import React from 'react';
import { useTranslation } from './i18n.js';
import { getCookieConsent, setCookieConsent } from './cookieConsent.js';

function CookiePreferencesModal({ consent, onSave, onClose }) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = React.useState(consent ? !!consent.analytics : false);
  const [marketing, setMarketing] = React.useState(consent ? !!consent.marketing : false);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="legal-modal-overlay" onClick={onClose}>
      <div
        className="legal-modal cookie-prefs-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('cookiePrefsTitle')}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="legal-modal-close"
          aria-label={t('footerCloseLabel')}
          onClick={onClose}
        >
          &times;
        </button>
        <h1>{t('cookiePrefsTitle')}</h1>
        <p>{t('cookiePrefsIntro')}</p>

        <div className="cookie-category">
          <div className="cookie-category-header">
            <span className="cookie-category-title">{t('cookieCategoryNecessaryTitle')}</span>
            <span className="cookie-always-active">{t('cookieAlwaysActive')}</span>
          </div>
          <p className="cookie-category-desc">{t('cookieCategoryNecessaryDesc')}</p>
        </div>

        <div className="cookie-category">
          <div className="cookie-category-header">
            <span className="cookie-category-title">{t('cookieCategoryAnalyticsTitle')}</span>
            <span className="cookie-toggle">
              <input
                type="checkbox"
                aria-label={t('cookieCategoryAnalyticsTitle')}
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
              <span className="cookie-toggle-slider" aria-hidden="true" />
            </span>
          </div>
          <p className="cookie-category-desc">{t('cookieCategoryAnalyticsDesc')}</p>
        </div>

        <div className="cookie-category">
          <div className="cookie-category-header">
            <span className="cookie-category-title">{t('cookieCategoryMarketingTitle')}</span>
            <span className="cookie-toggle">
              <input
                type="checkbox"
                aria-label={t('cookieCategoryMarketingTitle')}
                checked={marketing}
                onChange={(event) => setMarketing(event.target.checked)}
              />
              <span className="cookie-toggle-slider" aria-hidden="true" />
            </span>
          </div>
          <p className="cookie-category-desc">{t('cookieCategoryMarketingDesc')}</p>
        </div>

        <div className="cookie-prefs-actions">
          <button
            type="button"
            className="cookie-btn cookie-btn-secondary"
            onClick={() => onSave({ analytics: false, marketing: false })}
          >
            {t('cookieBannerRejectAll')}
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-secondary"
            onClick={() => onSave({ analytics, marketing })}
          >
            {t('cookiePrefsSave')}
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-primary"
            onClick={() => onSave({ analytics: true, marketing: true })}
          >
            {t('cookieBannerAcceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CookieConsentManager() {
  const { t } = useTranslation();
  // `null` des deux côtés au premier rendu (le serveur n'a pas de localStorage) : lire le vrai
  // consentement dès le rendu client casserait l'hydratation si un choix est déjà enregistré
  // (la bannière apparaîtrait/disparaîtrait entre le HTML serveur et le rendu client) — on le lit
  // dans un effet à la place.
  const [consent, setConsentState] = React.useState(null);
  const [prefsOpen, setPrefsOpen] = React.useState(false);

  React.useEffect(() => {
    setConsentState(getCookieConsent());
  }, []);

  React.useEffect(() => {
    const handleOpen = () => setPrefsOpen(true);
    window.addEventListener('open-cookie-preferences', handleOpen);
    return () => window.removeEventListener('open-cookie-preferences', handleOpen);
  }, []);

  const save = (nextConsent) => {
    const full = {
      ...nextConsent,
      necessary: true,
      decided: true,
      updatedAt: new Date().toISOString(),
    };
    setCookieConsent(full);
    setConsentState(full);
    setPrefsOpen(false);
  };

  const showBanner = !consent || !consent.decided;

  return (
    <>
      {showBanner && (
        <div className="cookie-banner" role="dialog" aria-label={t('cookiePrefsTitle')}>
          <p className="cookie-banner-message">{t('cookieBannerMessage')}</p>
          <div className="cookie-banner-actions">
            <button
              type="button"
              className="cookie-btn cookie-btn-secondary"
              onClick={() => setPrefsOpen(true)}
            >
              {t('cookieBannerCustomize')}
            </button>
            <button
              type="button"
              className="cookie-btn cookie-btn-secondary"
              onClick={() => save({ analytics: false, marketing: false })}
            >
              {t('cookieBannerRejectAll')}
            </button>
            <button
              type="button"
              className="cookie-btn cookie-btn-primary"
              onClick={() => save({ analytics: true, marketing: true })}
            >
              {t('cookieBannerAcceptAll')}
            </button>
          </div>
        </div>
      )}
      {prefsOpen && (
        <CookiePreferencesModal consent={consent} onSave={save} onClose={() => setPrefsOpen(false)} />
      )}
    </>
  );
}
